import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { createPortal } from 'react-dom'
import { Plus, Tag, Check, ToggleLeft, ToggleRight } from 'lucide-react'
import api from '../api/axios'
import toast from 'react-hot-toast'

// ─── Types ───────────────────────────────────────────────────────────────────
interface Plan { id: string; planName: string; jobCount: number; periodDays: number; price: number; description?: string; isActive: boolean; isTrial: boolean; trialFeatures?: string; trialBannerText?: string; trialMaxSignups?: number; isPopular?: boolean; unlimitedRevisions?: boolean; freeRevisions?: number; addonPrice?: number; complexJobsAllowed?: number }
interface Coupon { id: string; name: string; code: string; planScope: string; planIds: string[]; discountType: string; discountValue: number; validFrom: string | null; validTo: string | null; isActive: boolean; maxTotalUses: number; maxUsesPerClient: number; totalUses: number; createdBy: string; createdAt: string }

const EMPTY_COUPON_FORM = { name: '', code: '', planScope: 'all', planIds: [] as string[], discountType: 'percentage', discountValue: '', validFrom: '', validTo: '', isActive: true, maxTotalUses: '0', maxUsesPerClient: '0' }
const EMPTY_PLAN_FORM = { planName: '', jobCount: '', periodDays: '', price: '', description: '', isPopular: false, unlimitedRevisions: false, freeRevisions: '0', addonPrice: '0', complexJobsAllowed: '0', isTrial: false, trialFeatures: '', trialBannerText: '', trialMaxSignups: '0' }
const EMPTY_TRIAL_FORM = { planName: '', jobCount: '', periodDays: '3', description: '', trialBannerText: 'Start your free 3-day trial today — no credit card required!', trialFeatures: 'Full Portal Access,Submit Jobs via Email or Portal,Personal Artwork Dashboard,Secure File Storage,Priority Support', trialMaxSignups: '0' }

export default function InkDealsPage() {
  const { hasPermission } = useAuth()
  const [trialPlans, setTrialPlans] = useState<Plan[]>([])
  const [subPlans, setSubPlans] = useState<Plan[]>([])
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [allPlans, setAllPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [activePlanId, setActivePlanId] = useState<string>('')

  // Trial plan form
  const [showTrialForm, setShowTrialForm] = useState(false)
  const [editingTrialId, setEditingTrialId] = useState<string | null>(null)
  const [trialForm, setTrialForm] = useState({ ...EMPTY_TRIAL_FORM })
  const [savingTrial, setSavingTrial] = useState(false)

  // Sub plan form
  const [showSubForm, setShowSubForm] = useState(false)
  const [editingSubId, setEditingSubId] = useState<string | null>(null)
  const [subForm, setSubForm] = useState({ ...EMPTY_PLAN_FORM })
  const [savingSub, setSavingSub] = useState(false)
  const [subPage, setSubPage] = useState(1)
  const SUB_PER_PAGE = 5
  const [trialPage, setTrialPage] = useState(1)
  const [couponPage, setCouponPage] = useState(1)
  const TRIAL_PER_PAGE = 5
  const COUPON_PER_PAGE = 5

  // Coupon form
  const [showCouponForm, setShowCouponForm] = useState(false)
  const [editingCouponId, setEditingCouponId] = useState<string | null>(null)
  const [couponForm, setCouponForm] = useState({ ...EMPTY_COUPON_FORM })
  const [savingCoupon, setSavingCoupon] = useState(false)

  const fetchAll = useCallback(async () => {
    try {
      const [plansRes, couponsRes, settingsRes] = await Promise.all([
        api.get('/settings/plans'),
        api.get('/coupons'),
        api.get('/settings')
      ])
      const plans = plansRes.data
      setTrialPlans(plans.filter((p: Plan) => p.isTrial))
      setSubPlans(plans.filter((p: Plan) => !p.isTrial))
      setAllPlans(plans.filter((p: Plan) => !p.isTrial))
      setCoupons(couponsRes.data.coupons || [])

      const allSettings: any[] = []
      settingsRes.data.forEach((cat: any) => allSettings.push(...cat.settings))
      const trialPlanIdSetting = allSettings.find((s: any) => s.settingKey === 'trial_plan_id')
      setActivePlanId(trialPlanIdSetting?.settingValue || '')
    } catch {}
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // ─── Trial Plan Handlers ──────────────────────────────────────────────────
  const copyTrialLink = (planId: string) => {
    const base = window.location.origin
    navigator.clipboard.writeText(`${base}/trial?plan=${planId}`)
      .then(() => toast.success('Trial link copied to clipboard'))
      .catch(() => toast.error('Failed to copy link'))
  }

  const openNewTrial = () => { setEditingTrialId(null); setTrialForm({ ...EMPTY_TRIAL_FORM }); setShowTrialForm(true) }
  const openEditTrial = (p: Plan) => {
    setEditingTrialId(p.id)
    setTrialForm({ planName: p.planName, jobCount: p.jobCount.toString(), periodDays: p.periodDays.toString(), description: p.description || '', trialBannerText: p.trialBannerText || '', trialFeatures: p.trialFeatures || '', trialMaxSignups: p.trialMaxSignups?.toString() || '0' })
    setShowTrialForm(true)
  }
  const saveTrial = async () => {
    if (!trialForm.planName || !trialForm.jobCount || !trialForm.periodDays) { toast.error('Please fill required fields'); return }
    setSavingTrial(true)
    try {
      if (editingTrialId) {
        await api.patch(`/settings/plans/${editingTrialId}`, { ...trialForm, isTrial: true, price: 0 })
      } else {
        await api.post('/settings/plans', { ...trialForm, isTrial: true, price: 0 })
      }
      toast.success(editingTrialId ? 'Trial plan updated' : 'Trial plan created')
      setShowTrialForm(false)
      fetchAll()
    } catch (e: any) { toast.error(e.response?.data?.error || 'Failed to save') }
    setSavingTrial(false)
  }
  const deleteTrial = async (id: string) => {
    if (!window.confirm('Delete this trial plan?')) return
    try { await api.delete(`/settings/plans/${id}`); toast.success('Deleted'); fetchAll() }
    catch (e: any) { toast.error(e.response?.data?.error || 'Failed to delete') }
  }
  const setActiveTrial = async (id: string) => {
    await api.patch('/settings/trial_plan_id', { value: id })
    setActivePlanId(id)
    toast.success('Active trial plan updated')
  }
  const toggleTrialPlan = async (p: Plan) => {
    await api.patch(`/settings/plans/${p.id}`, { isActive: !p.isActive })
    fetchAll()
  }

  // ─── Sub Plan Handlers ────────────────────────────────────────────────────
  const openNewSub = () => { setEditingSubId(null); setSubForm({ ...EMPTY_PLAN_FORM }); setShowSubForm(true) }
  const openEditSub = (p: any) => {
  setEditingSubId(p.id)
  setSubForm({
    planName: p.planName,
    jobCount: p.jobCount.toString(),
    periodDays: p.periodDays.toString(),
    price: p.price.toString(),
    description: p.description || '',
    isPopular: p.isPopular || false,
    unlimitedRevisions: p.unlimitedRevisions || false,
    freeRevisions: p.freeRevisions?.toString() || '0',
    addonPrice: p.addonPrice?.toString() || '0',
    complexJobsAllowed: p.complexJobsAllowed?.toString() || '0',
    isTrial: false,
    trialFeatures: '',
    trialBannerText: '',
    trialMaxSignups: '0'
  })
  setShowSubForm(true)
}
  const saveSub = async () => {
    if (!subForm.planName || !subForm.jobCount || !subForm.periodDays || !subForm.price) { toast.error('Please fill required fields'); return }
    setSavingSub(true)
    try {
      if (editingSubId) {
        await api.patch(`/settings/plans/${editingSubId}`, { ...subForm, isTrial: false })
      } else {
        await api.post('/settings/plans', { ...subForm, isTrial: false })
      }
      toast.success(editingSubId ? 'Plan updated' : 'Plan created')
      setShowSubForm(false)
      fetchAll()
    } catch (e: any) { toast.error(e.response?.data?.error || 'Failed to save') }
    setSavingSub(false)
  }
  const deleteSub = async (id: string) => {
    if (!window.confirm('Delete this plan?')) return
    try { await api.delete(`/settings/plans/${id}`); toast.success('Deleted'); fetchAll() }
    catch (e: any) { toast.error(e.response?.data?.error || 'Failed to delete') }
  }
  const toggleSubPlan = async (p: Plan) => {
    await api.patch(`/settings/plans/${p.id}`, { isActive: !p.isActive })
    fetchAll()
  }

  // ─── Coupon Handlers ──────────────────────────────────────────────────────
  const openNewCoupon = () => { setEditingCouponId(null); setCouponForm({ ...EMPTY_COUPON_FORM }); setShowCouponForm(true) }
  const openEditCoupon = (c: Coupon) => {
    setEditingCouponId(c.id)
    setCouponForm({
      name: c.name, code: c.code, planScope: c.planScope, planIds: c.planIds,
      discountType: c.discountType, discountValue: c.discountValue.toString(),
      validFrom: c.validFrom ? c.validFrom.split('T')[0] : '',
      validTo: c.validTo ? c.validTo.split('T')[0] : '',
      isActive: c.isActive, maxTotalUses: c.maxTotalUses.toString(), maxUsesPerClient: c.maxUsesPerClient.toString()
    })
    setShowCouponForm(true)
  }
  const saveCoupon = async () => {
    if (!couponForm.name || !couponForm.code || !couponForm.discountValue) { toast.error('Please fill required fields'); return }
    if (couponForm.discountType === 'percentage' && Number(couponForm.discountValue) > 100) { toast.error('Percentage cannot exceed 100%'); return }
    setSavingCoupon(true)
    try {
      if (editingCouponId) {
        await api.patch(`/coupons/${editingCouponId}`, couponForm)
      } else {
        await api.post('/coupons', couponForm)
      }
      toast.success(editingCouponId ? 'Offer updated' : 'Offer created')
      setShowCouponForm(false)
      fetchAll()
    } catch (e: any) { toast.error(e.response?.data?.error || 'Failed to save') }
    setSavingCoupon(false)
  }
  const deleteCoupon = async (id: string) => {
    if (!window.confirm('Delete this offer?')) return
    try { await api.delete(`/coupons/${id}`); toast.success('Deleted'); fetchAll() }
    catch (e: any) { toast.error(e.response?.data?.error || 'Failed to delete') }
  }
  const toggleCoupon = async (c: Coupon) => {
    await api.patch(`/coupons/${c.id}`, { isActive: !c.isActive })
    fetchAll()
  }

  const formatDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-navy-900 dark:text-white flex items-center gap-3">
          <Tag size={28} className="text-orange-500" />
          InkDeals
        </h1>
        <p className="text-gray-400 mt-1 text-sm">Manage trial plans, subscription plans and offers</p>
      </div>

      {/* ─── Section 1: Trial Plans ─────────────────────────────────────────── */}
      {hasPermission('manage_trial_plans') && <div className="card overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-navy-900 dark:text-white">Free Trial Plans</h2>
            <p className="text-xs text-gray-400 mt-0.5">Plans available at <span className="font-mono text-orange-500">/trial</span></p>
          </div>
          <button onClick={openNewTrial} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm transition-colors flex-shrink-0">
            <Plus size={14} /> New Trial Plan
          </button>
        </div>

        {showTrialForm && (
          <div className="p-4 sm:p-5 border-b border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-[#1a2a40] space-y-4">
            <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">{editingTrialId ? 'Edit Trial Plan' : 'New Trial Plan'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="label">Plan Name *</label><input className="input text-sm" placeholder="e.g. 3-Day Free Trial" value={trialForm.planName} onChange={e => setTrialForm(f => ({ ...f, planName: e.target.value }))} /></div>
              <div><label className="label">Number of Jobs *</label><input className="input text-sm" type="number" placeholder="5" value={trialForm.jobCount} onChange={e => setTrialForm(f => ({ ...f, jobCount: e.target.value }))} /></div>
              <div><label className="label">Trial Period (Days) *</label><input className="input text-sm" type="number" placeholder="3" value={trialForm.periodDays} onChange={e => setTrialForm(f => ({ ...f, periodDays: e.target.value }))} /></div>
              <div><label className="label">Description</label><input className="input text-sm" placeholder="Brief description" value={trialForm.description} onChange={e => setTrialForm(f => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <div className="border border-gray-200 dark:border-[#1e3a5f] rounded-xl p-4 space-y-3 bg-white dark:bg-[#112240]">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Trial Page Settings</p>
              <div><label className="label">Banner Text</label><input className="input text-sm" placeholder="Start your free trial..." value={trialForm.trialBannerText} onChange={e => setTrialForm(f => ({ ...f, trialBannerText: e.target.value }))} /></div>
              <div><label className="label">What's Included (comma-separated)</label><textarea rows={3} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-orange-400" placeholder="Full Portal Access,Secure File Storage" value={trialForm.trialFeatures} onChange={e => setTrialForm(f => ({ ...f, trialFeatures: e.target.value }))} /></div>
              <div><label className="label">Max Signups <span className="text-gray-400 font-normal">(0 = unlimited)</span></label><input className="input text-sm w-28" type="number" value={trialForm.trialMaxSignups} onChange={e => setTrialForm(f => ({ ...f, trialMaxSignups: e.target.value }))} /></div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveTrial} disabled={savingTrial} className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl">{savingTrial ? 'Saving...' : editingTrialId ? 'Update' : 'Create'}</button>
              <button onClick={() => setShowTrialForm(false)} className="border border-gray-200 text-gray-500 px-4 py-2 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {trialPlans.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No trial plans yet.</div>
        ) : (
          <>
          <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
            {trialPlans.slice((trialPage - 1) * TRIAL_PER_PAGE, trialPage * TRIAL_PER_PAGE).map(p => (
              <div key={p.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-navy-800 dark:text-white">{p.planName}</p>
                    {activePlanId === p.id && <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">Active</span>}
                    {!p.isActive && <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{p.jobCount} jobs · {p.periodDays} days · Free{p.description ? ` · ${p.description}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => copyTrialLink(p.id)} className="text-xs px-3 py-1.5 border border-blue-200 text-blue-500 rounded-lg hover:bg-blue-50 flex items-center gap-1"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy Link</button>
                  {activePlanId !== p.id && <button onClick={() => setActiveTrial(p.id)} className="text-xs px-3 py-1.5 border border-green-200 text-green-600 rounded-lg hover:bg-green-50">Set Active</button>}
                  <button onClick={() => openEditTrial(p)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">Edit</button>
                  <button onClick={() => toggleTrialPlan(p)} className={`text-xs px-3 py-1.5 rounded-lg border ${p.isActive ? 'border-orange-200 text-orange-500 hover:bg-orange-50' : 'border-green-200 text-green-500 hover:bg-green-50'}`}>{p.isActive ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => deleteTrial(p.id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-400 hover:bg-red-50">Delete</button>
                </div>
              </div>
            ))}
          </div>
          {trialPlans.length > TRIAL_PER_PAGE && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-50 dark:border-[#1e3a5f]">
              <p className="text-xs text-gray-400">{trialPlans.length} plans · Page {trialPage} of {Math.ceil(trialPlans.length / TRIAL_PER_PAGE)}</p>
              <div className="flex gap-2">
                <button disabled={trialPage === 1} onClick={() => setTrialPage(p => p - 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                <button disabled={trialPage >= Math.ceil(trialPlans.length / TRIAL_PER_PAGE)} onClick={() => setTrialPage(p => p + 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>}
      {/* ─── Section 2: Subscription Plans ─────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-navy-900 dark:text-white">Subscription Plans</h2>
            <p className="text-xs text-gray-400 mt-0.5">Plans available at checkout</p>
          </div>
          <button onClick={openNewSub} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm transition-colors flex-shrink-0">
            <Plus size={14} /> New Plan
          </button>
        </div>

        {showSubForm && (
          <div className="p-4 sm:p-5 border-b border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-[#1a2a40] space-y-4">
            <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">{editingSubId ? 'Edit Plan' : 'New Plan'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="label">Plan Name *</label><input className="input text-sm" placeholder="e.g. Starter" value={subForm.planName} onChange={e => setSubForm(f => ({ ...f, planName: e.target.value }))} /></div>
              <div><label className="label">Price (USD) *</label><input className="input text-sm" type="number" placeholder="99" value={subForm.price} onChange={e => setSubForm(f => ({ ...f, price: e.target.value }))} /></div>
              <div><label className="label">Number of Jobs *</label><input className="input text-sm" type="number" placeholder="50" value={subForm.jobCount} onChange={e => setSubForm(f => ({ ...f, jobCount: e.target.value }))} /></div>
              <div><label className="label">Period (Days) *</label><input className="input text-sm" type="number" placeholder="30" value={subForm.periodDays} onChange={e => setSubForm(f => ({ ...f, periodDays: e.target.value }))} /></div>
              <div><label className="label">Free Revisions</label><input className="input text-sm" type="number" placeholder="0" value={subForm.freeRevisions} onChange={e => setSubForm(f => ({ ...f, freeRevisions: e.target.value }))} /></div>
              <div><label className="label">Add-on Price</label><input className="input text-sm" type="number" placeholder="0" value={subForm.addonPrice} onChange={e => setSubForm(f => ({ ...f, addonPrice: e.target.value }))} /></div>
              <div><label className="label">Complex Jobs Allowed</label><input className="input text-sm" type="number" placeholder="0" value={subForm.complexJobsAllowed} onChange={e => setSubForm(f => ({ ...f, complexJobsAllowed: e.target.value }))} /></div>
              <div><label className="label">Description</label><input className="input text-sm" placeholder="Brief description" value={subForm.description} onChange={e => setSubForm(f => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={subForm.unlimitedRevisions} onChange={e => setSubForm(f => ({ ...f, unlimitedRevisions: e.target.checked }))} className="accent-orange-500" />
                <span className="text-sm text-gray-600 dark:text-gray-300">Unlimited Revisions</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={subForm.isPopular} onChange={e => setSubForm(f => ({ ...f, isPopular: e.target.checked }))} className="accent-orange-500" />
                <span className="text-sm text-gray-600 dark:text-gray-300">Mark as Popular</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={saveSub} disabled={savingSub} className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl">{savingSub ? 'Saving...' : editingSubId ? 'Update' : 'Create'}</button>
              <button onClick={() => setShowSubForm(false)} className="border border-gray-200 text-gray-500 px-4 py-2 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {subPlans.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">No subscription plans yet.</div>
      ) : (
        <>
        <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
    {subPlans.slice((subPage - 1) * SUB_PER_PAGE, subPage * SUB_PER_PAGE).map(p => (
              <div key={p.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-navy-800 dark:text-white">{p.planName}</p>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">✓ Public</span>
                    {!p.isActive && <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full font-semibold">${p.price}</span>
                      <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{p.jobCount} jobs</span>
                      <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">{p.periodDays} days</span>
                      {Number(p.addonPrice) > 0 && (
                        <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">+${p.addonPrice} addon</span>
                      )}
                      {p.unlimitedRevisions ? (
                        <span className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full">∞ Revisions</span>
                      ) : Number(p.freeRevisions) > 0 ? (
                        <span className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full">{p.freeRevisions} Rev</span>
                      ) : null}
                      {Number(p.complexJobsAllowed) > 0 && (
                        <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">{p.complexJobsAllowed} Complex</span>
                      )}
                      {p.description && <span className="text-xs text-gray-400">{p.description}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => openEditSub(p)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">Edit</button>
                  <button onClick={() => toggleSubPlan(p)} className={`text-xs px-3 py-1.5 rounded-lg border ${p.isActive ? 'border-orange-200 text-orange-500 hover:bg-orange-50' : 'border-green-200 text-green-500 hover:bg-green-50'}`}>{p.isActive ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => deleteSub(p.id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-400 hover:bg-red-50">Delete</button>
                </div>
              </div>
            ))}
          </div>
          {subPlans.length > SUB_PER_PAGE && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-50 dark:border-[#1e3a5f]">
              <p className="text-xs text-gray-400">{subPlans.length} plans · Page {subPage} of {Math.ceil(subPlans.length / SUB_PER_PAGE)}</p>
              <div className="flex gap-2">
                <button disabled={subPage === 1} onClick={() => setSubPage(p => p - 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                <button disabled={subPage >= Math.ceil(subPlans.length / SUB_PER_PAGE)} onClick={() => setSubPage(p => p + 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* ─── Section 3: Pending Registrations ─────────────────────────────── */}
      <PendingRegistrationsSection />

      {/* ─── Section 4: Offers & Coupons ───────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-navy-900 dark:text-white">Offers & Coupons</h2>
            <p className="text-xs text-gray-400 mt-0.5">Create discount codes for clients</p>
          </div>
          <button onClick={openNewCoupon} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm transition-colors flex-shrink-0">
            <Plus size={14} /> New Offer
          </button>
        </div>

        {showCouponForm && (
          <div className="p-4 sm:p-5 border-b border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-[#1a2a40] space-y-4">
            <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">{editingCouponId ? 'Edit Offer' : 'New Offer'}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="label">Offer Name *</label><input className="input text-sm" placeholder="e.g. Summer Sale" value={couponForm.name} onChange={e => setCouponForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div>
                <label className="label">Coupon Code *</label>
                <div className="flex gap-2">
                  <input className="input text-sm font-mono uppercase" placeholder="e.g. SUMMER20" value={couponForm.code} onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
                  <button onClick={() => setCouponForm(f => ({ ...f, code: Math.random().toString(36).slice(2, 8).toUpperCase() }))} className="text-xs px-3 py-2 border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 flex-shrink-0">Auto</button>
                </div>
              </div>
            </div>

            {/* Plan scope */}
            <div>
              <label className="label">Applies To</label>
              <div className="flex gap-4 flex-wrap">
                {[{ value: 'all', label: 'All Plans' }, { value: 'specific', label: 'Specific Plans' }].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="planScope" value={opt.value} checked={couponForm.planScope === opt.value} onChange={() => setCouponForm(f => ({ ...f, planScope: opt.value, planIds: [] }))} className="accent-orange-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">{opt.label}</span>
                  </label>
                ))}
              </div>
              {couponForm.planScope === 'specific' && (
                <div className="mt-2 border border-gray-200 dark:border-[#1e3a5f] rounded-xl overflow-hidden max-h-40 overflow-y-auto">
                  {allPlans.map(p => (
                    <div key={p.id} onClick={() => setCouponForm(f => ({ ...f, planIds: f.planIds.includes(p.id) ? f.planIds.filter(id => id !== p.id) : [...f.planIds, p.id] }))}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-0">
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${couponForm.planIds.includes(p.id) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'}`}>
                        {couponForm.planIds.includes(p.id) && <Check size={12} className="text-white" />}
                      </div>
                      <span className="text-sm text-navy-800 dark:text-white">{p.planName}</span>
                      <span className="text-xs text-gray-400 ml-auto">${p.price}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Discount type */}
            <div>
              <label className="label">Discount Type</label>
              <div className="flex gap-2">
                {[{ value: 'percentage', label: '% Percentage' }, { value: 'amount', label: '$ Fixed Amount' }].map(opt => (
                  <button key={opt.value} onClick={() => setCouponForm(f => ({ ...f, discountType: opt.value }))}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all ${couponForm.discountType === opt.value ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-200 text-gray-500 hover:border-orange-300'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">{couponForm.discountType === 'percentage' ? 'Discount %' : 'Discount Amount ($)'} *</label>
                <input className="input text-sm" type="number" placeholder={couponForm.discountType === 'percentage' ? '20' : '10'} value={couponForm.discountValue} onChange={e => setCouponForm(f => ({ ...f, discountValue: e.target.value }))} />
                {couponForm.discountType === 'percentage' && Number(couponForm.discountValue) > 100 && (
                  <p className="text-xs text-red-500 mt-1">⚠ Percentage cannot exceed 100%</p>
                )}
              </div>
              <div>
                <label className="label">Active</label>
                <button onClick={() => setCouponForm(f => ({ ...f, isActive: !f.isActive }))}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${couponForm.isActive ? 'bg-green-50 border-green-200 text-green-600' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                  {couponForm.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  {couponForm.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="label">Valid From <span className="text-gray-400 font-normal">(optional)</span></label><input className="input text-sm" type="date" value={couponForm.validFrom} onChange={e => setCouponForm(f => ({ ...f, validFrom: e.target.value }))} /></div>
              <div><label className="label">Valid To <span className="text-gray-400 font-normal">(optional)</span></label><input className="input text-sm" type="date" value={couponForm.validTo} onChange={e => setCouponForm(f => ({ ...f, validTo: e.target.value }))} /></div>
              <div><label className="label">Max Total Uses <span className="text-gray-400 font-normal">(0 = unlimited)</span></label><input className="input text-sm" type="number" value={couponForm.maxTotalUses} onChange={e => setCouponForm(f => ({ ...f, maxTotalUses: e.target.value }))} /></div>
              <div><label className="label">Max Uses Per Client <span className="text-gray-400 font-normal">(0 = unlimited)</span></label><input className="input text-sm" type="number" value={couponForm.maxUsesPerClient} onChange={e => setCouponForm(f => ({ ...f, maxUsesPerClient: e.target.value }))} /></div>
            </div>

            <div className="flex gap-2">
              <button onClick={saveCoupon} disabled={savingCoupon} className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-xl">{savingCoupon ? 'Saving...' : editingCouponId ? 'Update Offer' : 'Create Offer'}</button>
              <button onClick={() => setShowCouponForm(false)} className="border border-gray-200 text-gray-500 px-4 py-2 rounded-xl text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {coupons.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No offers yet. Create your first coupon!</div>
        ) : (
          <>
          <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
            {coupons.slice((couponPage - 1) * COUPON_PER_PAGE, couponPage * COUPON_PER_PAGE).map(c => (
              <div key={c.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-navy-800 dark:text-white">{c.name}</p>
                    <span className="text-xs font-mono bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full border border-orange-100">{c.code}</span>
                    {c.isActive ? <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">Active</span> : <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {c.discountType === 'percentage' ? `${c.discountValue}% off` : `$${c.discountValue} off`}
                    {' · '}{c.planScope === 'all' ? 'All plans' : 'Specific plans'}
                    {' · '}{c.totalUses} uses
                    {c.maxTotalUses > 0 ? `/${c.maxTotalUses}` : ''}
                    {c.validTo ? ` · Expires ${formatDate(c.validTo)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => toggleCoupon(c)} className={`text-xs px-3 py-1.5 rounded-lg border ${c.isActive ? 'border-orange-200 text-orange-500 hover:bg-orange-50' : 'border-green-200 text-green-500 hover:bg-green-50'}`}>{c.isActive ? 'Deactivate' : 'Activate'}</button>
                  <button onClick={() => openEditCoupon(c)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">Edit</button>
                  <button onClick={() => deleteCoupon(c.id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-400 hover:bg-red-50">Delete</button>
                </div>
              </div>
            ))}
          </div>
          {coupons.length > COUPON_PER_PAGE && (
            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-50 dark:border-[#1e3a5f]">
              <p className="text-xs text-gray-400">{coupons.length} offers · Page {couponPage} of {Math.ceil(coupons.length / COUPON_PER_PAGE)}</p>
              <div className="flex gap-2">
                <button disabled={couponPage === 1} onClick={() => setCouponPage(p => p - 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                <button disabled={couponPage >= Math.ceil(coupons.length / COUPON_PER_PAGE)} onClick={() => setCouponPage(p => p + 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  )
}
function PendingRegistrationsSection() {
  const [failedActivations, setFailedActivations] = useState<any[]>([])
  const [abandonedCheckouts, setAbandonedCheckouts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [retrying, setRetrying] = useState<string | null>(null)
  const [selected, setSelected] = useState<any>(null)

  const fetchPending = async () => {
    try {
      const res = await api.get('/settings/pending-registrations')
      setFailedActivations(res.data.failedActivations || [])
      setAbandonedCheckouts(res.data.abandonedCheckouts || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchPending() }, [])

  const handleRetry = async (id: string) => {
    setRetrying(id)
    try {
      await api.post(`/settings/pending-registrations/${id}/retry`)
      toast.success('Registration retried successfully')
      fetchPending()
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Failed to retry')
    }
    setRetrying(null)
  }

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })

  const tableHeaders = () => (
    <thead className="bg-gray-50/70 dark:bg-[#0d1e35] border-b border-gray-100 dark:border-[#1e3a5f]">
      <tr>
        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Name</th>
        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Email</th>
        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Plan</th>
        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Date</th>
        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Action</th>
      </tr>
    </thead>
  )

  const tableRow = (p: any) => (
  <tr key={p.id} onClick={() => setSelected(p)} className="hover:bg-orange-50/30 dark:hover:bg-[#1a3050] cursor-pointer transition-colors">
      <td className="px-4 py-3">
        <p className="font-medium text-navy-800 dark:text-white text-sm">{p.name}</p>
        <p className="text-xs text-gray-400 sm:hidden">{p.email}</p>
      </td>
      <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{p.email}</td>
      <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{p.planName}</td>
      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap hidden sm:table-cell">{formatDate(p.createdAt)}</td>
      <td className="px-4 py-3 text-right">
        <button onClick={e => { e.stopPropagation(); handleRetry(p.id) }} disabled={retrying === p.id}
          className="text-xs px-3 py-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg transition-colors whitespace-nowrap">
          {retrying === p.id ? 'Retrying...' : 'Retry'}
        </button>
      </td>
    </tr>
  )

  return (
    <div className="card overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-navy-900 dark:text-white">Pending Registrations</h2>
          <p className="text-xs text-gray-400 mt-0.5">Clients who completed checkout but account creation failed</p>
        </div>
        <div className="flex gap-2">
          {failedActivations.length > 0 && <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full font-medium">{failedActivations.length} failed</span>}
          {abandonedCheckouts.length > 0 && <span className="text-xs bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full font-medium">{abandonedCheckouts.length} abandoned</span>}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : failedActivations.length === 0 && abandonedCheckouts.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">No pending registrations.</div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-[#1e3a5f]">
          {/* Failed Activations */}
          {failedActivations.length > 0 && (
            <div>
              <div className="px-4 sm:px-6 py-2 bg-red-50 dark:bg-red-900/10">
                <p className="text-xs font-semibold text-red-500 uppercase tracking-wide">Failed Activations — Payment received but account not created</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  {tableHeaders()}
                  <tbody className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                    {failedActivations.map(tableRow)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Abandoned Checkouts */}
          {abandonedCheckouts.length > 0 && (
            <div>
              <div className="px-4 sm:px-6 py-2 bg-yellow-50 dark:bg-yellow-900/10">
                <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide">Abandoned Checkouts — Started but not completed</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  {tableHeaders()}
                  <tbody className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                    {abandonedCheckouts.map(tableRow)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selected && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelected(null)}>
          <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
              <h2 className="font-display font-bold text-navy-900 dark:text-white">Pending Registration</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><p className="text-xs text-gray-400">Name</p><p className="text-sm font-medium text-navy-800 dark:text-white">{selected.name}</p></div>
                <div><p className="text-xs text-gray-400">Email</p><p className="text-sm text-navy-800 dark:text-white">{selected.email}</p></div>
                <div><p className="text-xs text-gray-400">Company</p><p className="text-sm text-navy-800 dark:text-white">{selected.companyName || '—'}</p></div>
                <div><p className="text-xs text-gray-400">Plan</p><p className="text-sm text-navy-800 dark:text-white">{selected.planName}</p></div>
                <div><p className="text-xs text-gray-400">Country</p><p className="text-sm text-navy-800 dark:text-white">{selected.country || '—'}</p></div>
                <div><p className="text-xs text-gray-400">City</p><p className="text-sm text-navy-800 dark:text-white">{selected.city || '—'}</p></div>
                <div><p className="text-xs text-gray-400">State</p><p className="text-sm text-navy-800 dark:text-white">{selected.state || '—'}</p></div>
                <div><p className="text-xs text-gray-400">ZIP</p><p className="text-sm text-navy-800 dark:text-white">{selected.zip || '—'}</p></div>
              </div>
              {selected.streetAddress && (
                <div><p className="text-xs text-gray-400">Street Address</p><p className="text-sm text-navy-800 dark:text-white">{selected.streetAddress}</p></div>
              )}
              <div><p className="text-xs text-gray-400">Registered</p><p className="text-sm text-navy-800 dark:text-white">{formatDate(selected.createdAt)}</p></div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1e3a5f] flex gap-3">
              <button onClick={async () => { await handleRetry(selected.id); setSelected(null) }}
                disabled={retrying === selected.id}
                className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
                {retrying === selected.id ? 'Retrying...' : 'Retry Registration'}
              </button>
              <button onClick={() => setSelected(null)}
                className="px-4 border border-gray-200 text-gray-500 rounded-xl hover:bg-gray-50 text-sm transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}